import React, { useState, useRef, useEffect } from 'react';
import search from '../../assets/image/search_white.svg';
import './GlobalSearch.scss';
import { useDispatch } from 'react-redux';
import { globalSearch } from '../../store/action/globalSearch';
import { useLocation, useNavigate } from 'react-router-dom';

const searchConfig = {
	Product: {
		filterBy: 'product_name',
		map: (ele) => ({ id: ele?.product_id, label: ele?.product_name }),
	},
	Lists: {
		filterBy: 'lists',
		map: (ele) => ({ id: ele?.id, label: ele?.name }),
	},
	ProductCode: {
		filterBy: 'product_code',
		map: (ele) => ({ id: ele?.product_id, label: ele?.product_code }),
	},
	Groups: { filterBy: 'group', map: (ele) => ({ id: ele?.product_group_id, label: ele?.name }) },
	Adjustment: {
		filterBy: 'adjustment',
		map: (ele) => ({ id: ele?.adjustment_id, label: ele?.reference_number }),
	},
	Vendor: { filterBy: 'vendor', map: (ele) => ({ id: ele?.vendor_id, label: ele?.vendor_name }) },
	'Purchase Order': {
		filterBy: 'purchase_order_number',
		map: (ele) => ({ id: ele?.purchase_order_id, label: ele?.purchase_order_number }),
	},
	'Purchase Receive': {
		filterBy: 'purchase_receive_number',
		map: (ele) => ({ id: ele?.purchase_receive_id, label: ele?.purchase_receive_number }),
	},
	'Purchase Bill': {
		filterBy: 'bill_number',
		map: (ele) => ({ id: ele?.purchase_bill_id, label: ele?.bill_number }),
	},
	'Payment Made': {
		filterBy: 'payment_number',
		map: (ele) => ({ id: ele?.payment_made_id, label: ele?.payment_number }),
	},
	'Credit Note': {
		filterBy: 'credit_note_number',
		map: (ele) => ({ id: ele?.credit_note_id, label: ele?.credit_note_number }),
	},
	Customers: { filterBy: 'customers', map: (ele) => ({ id: ele?.id, label: ele?.name }) },
	Doctors: { filterBy: 'doctors', map: (ele) => ({ id: ele?.id, label: ele?.name }) },
	Invoices: { filterBy: 'invoices', map: (ele) => ({ id: ele?.id, label: ele?.invoice_number }) },
	'Payments Received': {
		filterBy: 'payment_receives',
		map: (ele) => ({ id: ele?.id, label: ele?.payment_receive_number }),
	},
	'Sales Return': {
		filterBy: 'sales_return',
		map: (ele) => ({ id: ele?.id, label: ele?.return_number }),
	},
	'Sales Credit Note': {
		filterBy: 'sales_credit_note',
		map: (ele) => ({ id: ele?.id, label: ele?.credit_note_number }),
	},
};

const routeConfig = {
	Product: (id, suburl) => `/user/${suburl}/product-create/${id}`,
	Lists: (id, suburl) => `/user/${suburl}/lists`,
	ProductCode: (id, suburl) => `/user/${suburl}/product-create/${id}`,
	Groups: (id, suburl) => `/user/${suburl}/group-details/${id}`,
	Adjustment: (id, suburl) => `/user/${suburl}/inventory-adjustment-crete/${id}`,
	Vendor: (id, suburl) => `/user/${suburl}/vendor-create/${id}`,
	'Purchase Order': (id, suburl) => `/user/${suburl}/purchases-order-details/${id}`,
	'Purchase Receive': (id, suburl) => `/user/${suburl}/purchases-receive-details/${id}`,
	'Purchase Bill': (id, suburl) => `/user/${suburl}/purchases-bills-details/${id}`,
	'Payment Made': (id, suburl) => `/user/${suburl}/payment-made-details/${id}`,
	'Credit Note': (id, suburl) => `/user/${suburl}/credit-note-details/${id}`,
	Customers: (id, suburl) => `/user/${suburl}/customers-details/${id}`,
	Doctors: (id, suburl) => `/user/${suburl}/doctors-details/${id}`,
	Invoices: (id, suburl) => `/user/${suburl}/invoices-details/${id}`,
	'Payments Received': (id, suburl) => `/user/${suburl}/payments-received-details/${id}`,
	'Sales Return': (id, suburl) => `/user/${suburl}/sales-return-details/${id}`,
	'Sales Credit Note': (id, suburl) => `/user/${suburl}/sales/credit-note-details/${id}`,
};

const categories = [
	{ id: 'Product', label: 'Product' },
	{ id: 'Lists', label: 'Lists' },
	{ id: 'ProductCode', label: 'Product Code' },
	{ id: 'Groups', label: 'Groups' },
	{ id: 'Adjustment', label: 'Adjustment' },
	{ id: 'Vendor', label: 'Vendor' },
	{ id: 'Purchase Order', label: 'Purchase Order' },
	{ id: 'Purchase Receive', label: 'Purchase Receive' },
	{ id: 'Purchase Bill', label: 'Purchase Bill' },
	{ id: 'Payment Made', label: 'Payment Made' },
	{ id: 'Credit Note', label: 'Credit Note' },
	{ id: 'Customers', label: 'Customers' },
	{ id: 'Doctors', label: 'Doctors' },
	{ id: 'Invoices', label: 'Invoices' },
	{ id: 'Payments Received', label: 'Payments Received' },
	{ id: 'Sales Return', label: 'Sales Return' },
	{ id: 'Sales Credit Note', label: 'Sales Credit Note' },
];

const GlobalSearch = ({ inputRef }) => {
	const navigate = useNavigate();
	const location = useLocation();
	const [activebar, setActivebar] = useState('');
	const [searchQuery, setSearchQuery] = useState('');
	const [searchResults, setSearchResults] = useState([]);
	const [showResultsDropdown, setShowResultsDropdown] = useState(false);
	const [isSearchLoading, setIsSearchLoading] = useState(false);
	const [highlightedIndex, setHighlightedIndex] = useState(-1);
	const resultsRef = useRef(null);
	const dispatch = useDispatch();
	const [debounceTimeout, setDebounceTimeout] = useState(null);
	const [projectTitle, setProjectTitle] = useState('');

	useEffect(() => {
		const path = location?.pathname || '';
		const routeMap = [
			{ key: 'product', bar: 'Product', title: 'Product', createTitle: 'Create Product' },
			{ key: 'group', bar: 'Groups', title: 'Groups', createTitle: 'Group Details' },
			{
				key: 'inventory-adjustment',
				bar: 'Adjustment',
				title: 'Adjustment',
				createTitle: 'Create Adjustment',
			},
			{ key: 'vendor', bar: 'Vendor', title: 'Vendor', createTitle: 'Create Vendor' },
			{
				key: 'purchases-order',
				bar: 'Purchase Order',
				title: 'Purchase Order',
				createTitle: 'Create New Purchase Order',
			},
			{
				key: 'purchases-receive',
				bar: 'Purchase Receive',
				title: 'Purchase Receive',
				createTitle: 'Create New Purchase Receive',
			},
			{
				key: 'purchases-bills',
				bar: 'Purchase Bill',
				title: 'Purchase Bill',
				createTitle: 'Create New Purchase Bill',
			},
			{
				key: 'payment-made',
				bar: 'Payment Made',
				title: 'Payment Made',
				createTitle: 'Create New Payment Made',
			},
			{
				key: 'sales/credit-note',
				bar: 'Sales Credit Note',
				title: 'Sales Credit Note',
				createTitle: 'Create New Sales Credit Note',
			},
			{
				key: 'credit-note',
				bar: 'Credit Note',
				title: 'Credit Note',
				createTitle: 'Create New Credit Note',
			},
			{
				key: 'customers',
				bar: 'Customers',
				title: 'Customers',
				createTitle: 'Create New Customers',
			},
			{ key: 'doctors', bar: 'Doctors', title: 'Doctors', createTitle: 'Create New Doctors' },
			{ key: 'invoices', bar: 'Invoices', title: 'Invoices', createTitle: 'Create New Invoices' },
			{
				key: 'payments-received',
				bar: 'Payments Received',
				title: 'Payments Received',
				createTitle: 'Create New Payments Received',
			},
			{
				key: 'sales-return',
				bar: 'Sales Return',
				title: 'Sales Return',
				createTitle: 'Create New Sales Return',
			},
		];

		for (const route of routeMap) {
			if (path.includes(route.key)) {
				setActivebar(route.bar);
				if (path.includes('create') || path.includes('details')) {
					setProjectTitle(route.createTitle);
				} else {
					setProjectTitle(route.title);
				}
				break;
			}
		}
	}, [location?.pathname]);

	const handleSearchChange = (event) => {
		const value = event.target.value;
		setSearchQuery(value);
		setIsSearchLoading(true);

		if (debounceTimeout) {
			clearTimeout(debounceTimeout);
		}

		setDebounceTimeout(
			setTimeout(() => {
				if (!activebar) return;

				const cfg = searchConfig[activebar];
				if (!cfg) return;

				const param = `filterValue=${value}&filterBy=${cfg.filterBy}`;
				setSearchResults([]);

				dispatch(globalSearch(param)).then((res) => {
					if (res?.success) {
						const data = res?.data?.map(cfg.map) || [];
						setSearchResults(data);
					} else {
						setSearchResults([]);
					}
				});

				const results = searchResults.filter((item) =>
					item?.label?.toLowerCase()?.includes(value?.toLowerCase()),
				);

				setSearchResults(results);
				setIsSearchLoading(false);
				setShowResultsDropdown(true);
				setHighlightedIndex(-1);
			}, 300),
		);
	};

	const handleSeachBar = (param) => {
		setSearchResults([]);
		setActivebar(param);
	};

	const handleKeyDown = (event) => {
		if (event.key === 'ArrowDown') {
			setHighlightedIndex((prevIndex) => {
				const newIndex = (prevIndex + 1) % searchResults.length;
				scrollIntoView(newIndex);
				scrollToItem(newIndex);
				return newIndex;
			});
		} else if (event.key === 'ArrowUp') {
			setHighlightedIndex((prevIndex) => {
				const newIndex = (prevIndex + searchResults.length - 1) % searchResults.length;
				scrollIntoView(newIndex);
				scrollToItem(newIndex);
				return newIndex;
			});
		} else if (event.key === 'Enter') {
			if (highlightedIndex >= 0) {
				handleRouteTopage(searchResults[highlightedIndex]);
			}
		}
	};

	// 		if (itemOffset < scrollTop) {
	// 			resultsContainer.scrollTop = itemOffset;
	// 		} else if (itemOffset + itemHeight > scrollTop + containerHeight) {
	// 			resultsContainer.scrollTop = itemOffset + itemHeight - containerHeight;
	// 		}
	// 	}
	// };
	const scrollIntoView = (index) => {
		const container = resultsRef.current;

		if (!container) return;

		const item = container.children[index];

		if (!item) return;

		const itemTop = item.offsetTop;
		const itemBottom = itemTop + item.offsetHeight;

		const containerTop = container.scrollTop;
		const containerBottom = containerTop + container.clientHeight;

		// Scroll up
		if (itemTop < containerTop) {
			container.scrollTop = itemTop;
		}

		// Scroll down
		else if (itemBottom > containerBottom) {
			container.scrollTop = itemBottom - container.clientHeight;
		}
	};

	const handleClickOutside = (event) => {
		if (
			inputRef.current &&
			!inputRef.current.contains(event.target) &&
			resultsRef.current &&
			!resultsRef.current.contains(event.target)
		) {
			setShowResultsDropdown(false);
		}
	};

	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	useEffect(() => {
		document.title = `${projectTitle} | Pharmnex`;
	}, [projectTitle]);

	useEffect(() => {
		inputRef.current.focus();
	}, []);

	const handleRouteTopage = (data) => {
		if (routeConfig[activebar]) {
			navigate(routeConfig[activebar](data.id));
		}
		setSearchQuery('');
		setSearchResults([]);
		setShowResultsDropdown(false);
	};

	const scrollToItem = (index) => {
		const container = resultsRef.current;

		if (!container) return;

		const item = container.children[index];

		if (item) {
			item.scrollIntoView({
				block: 'nearest',
			});
		}
	};

	return (
		<div className="input-group form-mobile-check left-search w-auto">
			<div className="input-group-prepend">
				<div className="input-search-box p-0">
					<div className="dropdown text-end">
						<a
							className="d-block link-dark text-decoration-none dropdown-toggle px-3"
							id="dropdownUser1"
							data-bs-toggle="dropdown"
							aria-expanded="false"
							href="/"
						>
							<img
								src={search}
								alt="search-icon"
								width="20"
								height="20"
								className="rounded-circle me-2"
							/>
						</a>
						<ul
							className="dropdown-menu font-14"
							aria-labelledby="dropdownUser1"
						>
							{categories.map((category, index) => (
								<li key={category.id}>
									<div
										className={`dropdown-item py-2 ${activebar === category.id ? 'active' : ''} ${index === 0 ? 'border-top-0' : ''}`}
										onClick={() => handleSeachBar(category.id)}
									>
										{category.label}
									</div>
								</li>
							))}
						</ul>
					</div>
				</div>
			</div>
			<div className="col-lg-auto">
				<input
					ref={inputRef}
					type="search"
					className="form-control"
					style={{
						minWidth: '250px',
						borderLeft: '0',
						borderTopLeftRadius: '0',
						borderBottomLeftRadius: '0',
					}}
					placeholder={`Search in ${activebar}`}
					aria-label="Search"
					value={searchQuery}
					onChange={handleSearchChange}
					onKeyDown={handleKeyDown}
					onClick={handleSearchChange}
				/>

				{showResultsDropdown && (
					<div
						className="dropdown-menu show font-14"
						ref={resultsRef}
						style={{
							width: '250px',
							position: 'absolute',
							zIndex: 1000,
							maxHeight: '300px',
							overflowY: 'auto',
							backgroundColor: '#fff',
						}}
					>
						{isSearchLoading && <div className="text-center py-2">Loading....</div>}
						{!isSearchLoading &&
							searchResults.length > 0 &&
							searchResults?.map((result, index) => (
								<div
									key={result.id}
									className={`dropdown-item border-top cursor-pointer p-2 
                                ${index === 0 ? 'border-top-0' : ''}
                                ${index === highlightedIndex ? 'active bg-blue' : ''}`}
									onMouseEnter={() => setHighlightedIndex(index)}
									onClick={() => handleRouteTopage(result)}
								>
									{result.label}
								</div>
							))}

						{searchQuery.length > 0 && searchResults.length === 0 && !isSearchLoading && (
							<div className={`dropdown-item text-center py-2`}>{'No option found'}</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
};

export default GlobalSearch;
